import type { AnnotationLabelT } from '../../common/types/types';
import { type BoundingInput, type CanvasOffset, type MoveDirection } from '../editor-canvas.react';
import type { Annotation } from '../image-label.react';
import type { AnnotationType } from '../labelling/constants';
import { ANNOTATION_TYPE, CUSTOM_TOOLS } from '../labelling/constants';
export function calculateBboxForResize(point: [number, number], meta: BoundingInput, direction: MoveDirection) {
  const bbox = {};
  switch (direction) {
    case 'top-left':
      {
        const height = meta.height + meta.y - point[1];
        const width = meta.width + meta.x - point[0];
        if (height > 0 && width > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = height;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'y' does not exist on type '{}'.
          bbox.y = point[1];
          // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
          bbox.width = width;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'x' does not exist on type '{}'.
          bbox.x = point[0];
        }
        break;
      }
    case 'top-right':
      {
        const height = meta.height + meta.y - point[1];
        const width = point[0] - meta.x;
        if (height > 0 && width > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = height;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'y' does not exist on type '{}'.
          bbox.y = point[1];
          // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
          bbox.width = width;
        }
        break;
      }
    case 'bottom-right':
      {
        const height = point[1] - meta.y;
        const width = point[0] - meta.x;
        if (height > 0 && width > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = height;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
          bbox.width = width;
        }
        break;
      }
    case 'bottom-left':
      {
        const height = point[1] - meta.y;
        const width = meta.width + meta.x - point[0];
        if (height > 0 && width > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = height;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
          bbox.width = width;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'x' does not exist on type '{}'.
          bbox.x = point[0];
        }
        break;
      }
    case 'top':
      {
        const height = meta.height + meta.y - point[1];
        if (height > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = height;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'y' does not exist on type '{}'.
          bbox.y = point[1];
        }
        break;
      }
    case 'left':
      {
        const width = meta.width + meta.x - point[0];
        if (width > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
          bbox.width = width;
          // @ts-expect-error todo(ts-migration) TS2339 Property 'x' does not exist on type '{}'.
          bbox.x = point[0];
        }
        break;
      }
    case 'bottom':
      {
        const height = point[1] - meta.y;
        if (height > 0) {
          // @ts-expect-error todo(ts-migration) TS2339 Property 'height' does not exist on type '{}'.
          bbox.height = point[1] - meta.y;
        }
        break;
      }
    case 'right':
      const width = point[0] - meta.x;
      if (width > 0) {
        // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type '{}'.
        bbox.width = width;
      }
      break;
    default:
      break;
  }
  return bbox;
}
export function adjustOffsetsForReference(anchor: {
  x: number;
  y: number;
}, scaleCurrent: number, scaleNew: number, currentOffsets: CanvasOffset): CanvasOffset {
  /*
    When we adjust the scale, we will also have to offset the canvas
    if we want to zoom from the center. The scale transformation origin
    is at (0, 0) i.e. top left corner. If we scale without offsetting, it will
    zoom at the top left corner.
     To zoom from the anchor point, we find out how much the anchor has shifted because of
    the scale transformation. Then we offset the canvas to counteract that shift.
     ScreenPixel refers to a point based on its coordinates after transformations, i.e.
    what you seen on the screen.
     Reference refers to a point based on its original untransformed coordinates, e.g.
    the original points of an image.
  */
  const centerXScreenPixel = anchor.x;
  const centerXReferenceCurrent = centerXScreenPixel / scaleCurrent;
  const centerXReferenceNew = centerXScreenPixel / scaleNew;
  const cetnerXReferenceAdjustment = centerXReferenceNew - centerXReferenceCurrent;
  const centerYScreenPixel = anchor.y;
  const centerYReferenceCurrent = centerYScreenPixel / scaleCurrent;
  const centerYReferenceNew = centerYScreenPixel / scaleNew;
  const centerYReferenceAdjustment = centerYReferenceNew - centerYReferenceCurrent;
  return {
    x: currentOffsets.x + cetnerXReferenceAdjustment,
    y: currentOffsets.y + centerYReferenceAdjustment
  };
}
type Dimension = {
  width: number;
  height: number;
};
type Offset = {
  x: number;
  y: number;
};
const DEFAULT_PADDING = {
  x: 20,
  y: 20
};

// Calculate scale and offset to contain the image within the container
export function calculateScaleOffsetToFit(imgSize: Dimension, containerSize: Dimension, padding: Offset = DEFAULT_PADDING): {
  scale: number;
  offset: Offset;
} {
  const container = {
    width: containerSize.width - padding.x,
    height: containerSize.height - padding.y
  };
  let scale, offset;
  const widthRatio = container.width / imgSize.width;
  const heightRatio = container.height / imgSize.height;
  if (widthRatio < heightRatio) {
    // max size limited by width
    scale = widthRatio;
    offset = {
      x: padding.x / scale / 2,
      y: (container.height / scale - imgSize.height) / 2
    };
  } else {
    // max size limited by height
    scale = heightRatio;
    offset = {
      x: (container.width / scale - imgSize.width) / 2,
      y: padding.y / scale / 2
    };
  }
  return {
    scale,
    offset
  };
}
export function rotateAnnotationsByNinetyDegree(newRotation: number, imgSize: {
  width: number;
  height: number;
}, annotations: Annotation[]) {
  const newImgSize = newRotation % 180 ? {
    ...imgSize
  } : {
    width: imgSize.height,
    height: imgSize.width
  };
  return annotations.map<Annotation>(annotation => {
    const bbMeta = annotation.meta;
    let newMeta = {
      ...bbMeta,
      x: newImgSize.height - bbMeta.y - bbMeta.height,
      y: bbMeta.x,
      width: bbMeta.height,
      height: bbMeta.width
    };
    return {
      ...annotation,
      meta: newMeta
    };
  });
}
export function convertToAnnotationType(annotationType: string): AnnotationType | null {
  switch (annotationType) {
    case ANNOTATION_TYPE.RECTANGLE:
      return ANNOTATION_TYPE['RECTANGLE'];
    case ANNOTATION_TYPE.CUBOID:
      return ANNOTATION_TYPE['CUBOID'];
    case ANNOTATION_TYPE.ELLIPSE:
      return ANNOTATION_TYPE['ELLIPSE'];
    case ANNOTATION_TYPE.POLYGON:
      return ANNOTATION_TYPE['POLYGON'];
    case ANNOTATION_TYPE.POLYLINE:
      return ANNOTATION_TYPE['POLYLINE'];
    case ANNOTATION_TYPE.KEY_POINT:
      return ANNOTATION_TYPE['KEY_POINT'];
    case ANNOTATION_TYPE.SEGMENTATION:
      return ANNOTATION_TYPE['SEGMENTATION'];
    case ANNOTATION_TYPE.SKELETON:
      return ANNOTATION_TYPE['SKELETON'];
    case ANNOTATION_TYPE.FRAME_CLASSIFICATION:
      return ANNOTATION_TYPE['FRAME_CLASSIFICATION'];
    default:
      console.error(`Invalid annotation type: ${annotationType}. Expected one of:`, Object.values(ANNOTATION_TYPE));
      return null;
  }
}
export function filterAnnotationLabelsByType(annotationType: AnnotationType | null | undefined, annotationLabels: AnnotationLabelT[]) {
  if (!annotationType) {
    return [];
  }
  if (annotationType in CUSTOM_TOOLS) {
    return annotationLabels.filter(label => label.tools?.includes(annotationType));
  }
  return annotationLabels.filter(label => !label.tools || label.tools.includes(annotationType));
}